import type { IRewardsService } from '../../services/rewards/IRewardsService';
import type { INonsoleReadinessService } from '../../services/nonsoleReadiness/INonsoleReadinessService';
import type { RewardsProfile, CatalogueSku, RewardClaim, ActivityResponse } from '../../services/rewards/types';
import { ProfileManager } from './managers/ProfileManager';
import { CatalogueManager } from './managers/CatalogueManager';
import { RestockManager } from './managers/RestockManager';
import { HostMessagesManager } from './managers/HostMessagesManager';
import { ActivityManager, type ActivityState } from './managers/ActivityManager';
import { AffordabilityManager } from './managers/AffordabilityManager';
import { RedeemController } from './RedeemController';
import { HostModeEntryController } from './HostModeEntryController';

export interface RewardsState {
  profile: RewardsProfile | null;
  profileLoading: boolean;
  profileError: string | null;
  catalogue: CatalogueSku[];
  catalogueLoading: boolean;
  catalogueError: string | null;
  claims: RewardClaim[];
  hostMessages: string[];
  hostMessageIndex: number;
  hostMessageVisible: boolean;
  restockCountdown: { label: string; localDate: string } | null;
  unreadNotifications: number;
  affordableItem: CatalogueSku | null;
  showAffordabilityModal: boolean;
}

export type RewardsStateListener = (state: RewardsState) => void;

export interface RewardsCoordinatorDeps {
  rewardsService: IRewardsService;
  nonsoleReadinessService: INonsoleReadinessService;
}

export class RewardsCoordinator {
  private readonly rewardsService: IRewardsService;
  private readonly nonsoleReadinessService: INonsoleReadinessService;
  private readonly profileManager: ProfileManager;
  private readonly catalogueManager: CatalogueManager;
  private readonly restockManager: RestockManager;
  private readonly hostMessagesManager: HostMessagesManager;
  private readonly activityManager: ActivityManager;
  private readonly affordabilityManager: AffordabilityManager;

  private claims: RewardClaim[] = [];
  private listeners: RewardsStateListener[] = [];
  private unsubscribers: Array<() => void> = [];
  private destroyed = false;

  constructor(deps: RewardsCoordinatorDeps) {
    this.rewardsService = deps.rewardsService;
    this.nonsoleReadinessService = deps.nonsoleReadinessService;
    this.profileManager = new ProfileManager(this.rewardsService);
    this.catalogueManager = new CatalogueManager(this.rewardsService);
    this.restockManager = new RestockManager(this.rewardsService);
    this.hostMessagesManager = new HostMessagesManager(this.rewardsService);
    this.activityManager = new ActivityManager(this.rewardsService);
    this.affordabilityManager = new AffordabilityManager();
  }

  init(): void {
    this.profileManager.init();
    this.profileManager.fetchProfile();
    this.catalogueManager.fetchCatalogue();
    this.restockManager.init();
    this.hostMessagesManager.init();
    this.fetchClaims();

    this.unsubscribers.push(
      this.profileManager.subscribe(() => {
        this.runAffordabilityCheck();
        this.notify();
      }),
      this.catalogueManager.subscribe(() => {
        this.runAffordabilityCheck();
        this.notify();
      }),
      this.restockManager.subscribe(() => this.notify()),
      this.hostMessagesManager.subscribe(() => this.notify()),
      this.affordabilityManager.subscribe(() => this.notify()),
    );
  }

  destroy(): void {
    this.destroyed = true;
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.profileManager.destroy();
    this.catalogueManager.destroy();
    this.restockManager.destroy();
    this.hostMessagesManager.destroy();
    this.activityManager.destroy();
    this.affordabilityManager.destroy();
    this.listeners = [];
  }

  getState(): RewardsState {
    const profile = this.profileManager.getState();
    const catalogue = this.catalogueManager.getState();
    const restock = this.restockManager.getState();
    const messages = this.hostMessagesManager.getState();
    const affordability = this.affordabilityManager.getState();

    return {
      profile: profile.profile,
      profileLoading: profile.loading,
      profileError: profile.error,
      catalogue: catalogue.items,
      catalogueLoading: catalogue.loading,
      catalogueError: catalogue.error,
      claims: this.claims,
      hostMessages: messages.messages,
      hostMessageIndex: messages.currentIndex,
      hostMessageVisible: messages.visible,
      restockCountdown: restock.loading ? null : { label: restock.label, localDate: restock.localDate },
      unreadNotifications: profile.profile?.unreadNotifications ?? 0,
      affordableItem: affordability.affordableItem,
      showAffordabilityModal: affordability.visible,
    };
  }

  subscribe(listener: RewardsStateListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async refreshProfile(): Promise<void> {
    await this.profileManager.fetchProfile();
  }

  async refreshCatalogue(): Promise<void> {
    await this.catalogueManager.fetchCatalogue();
  }

  async refreshMessages(): Promise<void> {
    await this.hostMessagesManager.refresh();
  }

  setMachineTierId(tierId: string | null): void {
    this.hostMessagesManager.setMachineTierId(tierId);
  }

  async refreshRestock(): Promise<void> {
    await this.restockManager.refreshSchedule();
  }

  async refreshClaims(): Promise<void> {
    await this.fetchClaims();
  }

  async getActivity(limit: number, offset: number): Promise<ActivityResponse> {
    return this.rewardsService.getActivity(limit, offset);
  }

  getActivityManager(): ActivityManager {
    return this.activityManager;
  }

  dismissAffordability(): void {
    this.affordabilityManager.dismiss();
  }

  createRedeemFlow(skuId: string): RedeemController {
    return new RedeemController({
      skuId,
      rewardsService: this.rewardsService,
      onComplete: () => {
        this.refreshProfile();
        this.refreshCatalogue();
        this.refreshClaims();
      },
    });
  }

  createHostModeEntryFlow(): HostModeEntryController {
    return new HostModeEntryController({
      nonsoleReadinessService: this.nonsoleReadinessService,
    });
  }

  private runAffordabilityCheck(): void {
    const balance = this.profileManager.getState().profile?.pointsBalance ?? 0;
    const catalogue = this.catalogueManager.getState().items;
    this.affordabilityManager.check(balance, catalogue);
  }

  private async fetchClaims(): Promise<void> {
    try {
      const data = await this.rewardsService.getClaims();
      if (this.destroyed) return;
      if (Array.isArray(data)) {
        this.claims = data;
        this.notify();
      }
    } catch {
      // Non-critical failure
    }
  }

  private notify(): void {
    if (this.destroyed) return;
    const state = this.getState();
    for (const listener of this.listeners) {
      listener(state);
    }
  }
}
